import React from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Button,
    Divider,
    Switch,
    FormControlLabel,
    Collapse,
    TablePagination,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import SolarPanel from '../../../assets/images/solar-panel.png'
import Lighting from '../../../assets/images/lighting.png'
import BugReportIcon from '@mui/icons-material/BugReport'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import MapIcon from '@mui/icons-material/Map'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FilterListIcon from '@mui/icons-material/FilterList'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined'

const MetricItem = ({ label, value, icon, color = 'text.secondary' }) => (
    <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
            {icon && <Box sx={{ mr: 0.5, display: 'flex' }}>{icon}</Box>}
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            {value}
        </Typography>
    </Box>
)

const PlantCard = ({ row, onClick, issueCount, handleViewIssues, isMonitoring, deviceStatusForRow }) => {
    return (
        <Card
            sx={{
                mb: 2,
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid #f0f0f0',
                overflow: 'hidden',
                position: 'relative',
                '&:active': { bgcolor: '#fcfcfc' }
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, mb: 0.5 }}>
                            {row.plantName} <Typography component="span" variant="caption" sx={{ color: '#666', fontWeight: 600, ml: 1, bgcolor: '#f0f0f0', px: 1, py: 0.2, borderRadius: '4px' }}>{row.projectCode}</Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                            <LocationOnIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: '#999' }} /> {row.plantLocation}
                        </Typography>
                    </Box>
                    <IconButton size="small" sx={{ bgcolor: '#f8f9fa', borderRadius: '10px' }}>
                        <KeyboardArrowRightIcon />
                    </IconButton>
                </Box>

                <Divider sx={{ my: 1.5, borderColor: '#f0f0f0' }} />

                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <MetricItem
                            label="Size"
                            value={`${row.plantSizeKW || 0} kWp`}
                            icon={<img src={SolarPanel} alt="" style={{ width: '12px' }} />}
                        />
                    </Grid>
                    {isMonitoring ? (
                        <>
                            <Grid item xs={4}>
                                <MetricItem
                                    label="Daily Energy"
                                    value={row.totalEToday !== null ? parseFloat(row.totalEToday).toFixed(1) : 0}
                                    icon={<img src={Lighting} alt="" style={{ width: '12px' }} />}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <MetricItem
                                    label="E. Today"
                                    value={row.dailyEnergy !== null ? parseFloat(row.dailyEnergy).toFixed(1) : 0}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <MetricItem label="Yield" value={row.yield !== null ? parseFloat(row.yield).toFixed(2) : 0} />
                            </Grid>
                            <Grid item xs={4}>
                                <MetricItem label="GTI" value={parseFloat(row.gti || 0).toFixed(2)} />
                            </Grid>
                            <Grid item xs={4}>
                                <MetricItem label="PR" value={`${parseFloat(row.PR || 0).toFixed(1)}%`} />
                            </Grid>
                            <Grid item xs={4}>
                                <MetricItem label="CUF" value={`${parseFloat(row.CUF || 0).toFixed(1)}%`} />
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>Grid</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: deviceStatusForRow?.gridStatus === 'online' ? '#4caf50' : '#f44336', mr: 0.8 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{deviceStatusForRow?.gridStatus || 'Offline'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>Device</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                                        <RadioButtonCheckedOutlinedIcon sx={{ fontSize: '1rem', color: deviceStatusForRow?.overallDeviceStatus === 'green' ? '#4caf50' : (deviceStatusForRow?.overallDeviceStatus === 'red' ? '#f44336' : '#ff9800'), mr: 0.5 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{deviceStatusForRow?.overallDeviceStatus || 'Unknown'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={4}>
                                <MetricItem label="Comm. Date" value={row.commissionDate ? new Date(row.commissionDate).toLocaleDateString() : '-'} />
                            </Grid>
                        </>
                    )}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: issueCount > 0 ? '#fff1f0' : '#f5f5f5',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '20px',
                                border: `1px solid ${issueCount > 0 ? '#ffccc7' : '#e8e8e8'}`,
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewIssues(row.id, row.plantName);
                            }}
                        >
                            <BugReportIcon sx={{ fontSize: '1rem', color: issueCount > 0 ? '#f5222d' : '#999', mr: 0.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: issueCount > 0 ? '#cf1322' : '#666' }}>
                                {issueCount || 0} Issue(s)
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}

const SolarPlantListMobileView = ({ logic, permissions }) => {
    const {
        currentRows,
        loading,
        isMonitoring,
        handleMonitoringToggle,
        searchName,
        handleSearch,
        sortDate,
        handleChangeDate,
        issueCounts,
        handleViewIssues,
        handleTechnicalView,
        handleAddnewplant,
        handleADDdevice,
        handlePlantreport,
        handleSolarMapView,
        selectedValue,
        handleSelectChange,
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        totalPlants,
        deviceStatus
    } = logic

    const { is, can } = permissions

    const [expandedFilters, setExpandedFilters] = React.useState(false)

    return (
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', minHeight: '100vh', pb: 10 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                    Solar Plant List
                </Typography>
            </Box>

            {/* Mobile Search & Filter */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                {/* Search Bar */}
                <TextField
                    fullWidth
                    placeholder="Search solar plants..."
                    value={searchName}
                    onChange={handleSearch}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#999' }} />
                            </InputAdornment>
                        ),
                        sx: { bgcolor: '#fff', borderRadius: '12px', '& fieldset': { borderColor: '#eee' } }
                    }}
                />

                {/* Row 1: Date Selector (full width) */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        format="DD-MM-YYYY"
                        value={sortDate}
                        onChange={handleChangeDate}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                sx: {
                                    bgcolor: '#fff',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: '#eee' }
                                }
                            }
                        }}
                    />
                </LocalizationProvider>

                <Button
                    onClick={() => setExpandedFilters(!expandedFilters)}
                    variant="text"
                    startIcon={<FilterListIcon />}
                    endIcon={expandedFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    sx={{
                        color: '#666',
                        textTransform: 'none',
                        fontWeight: 600,
                        alignSelf: 'flex-start',
                        py: 0
                    }}
                >
                    {expandedFilters ? 'Hide Filters' : 'More Filters'}
                </Button>

                <Collapse in={expandedFilters}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        {/* Row 2: Device List + Monitored Toggle */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(is('Super Admin') || is('Admin') || is('Asset Management')) && (
                                <Button
                                    variant="contained"
                                    onClick={handleADDdevice}
                                    sx={{
                                        flex: 1,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        bgcolor: '#1976d2',
                                        '&:hover': { bgcolor: '#1565c0' },
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Device List
                                </Button>
                            )}
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                bgcolor: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #eee',
                                px: 1.5
                            }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={isMonitoring}
                                            onChange={handleMonitoringToggle}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1976d2' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1976d2' }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                            {isMonitoring ? 'Monitored' : 'Non-Monitored'}
                                        </Typography>
                                    }
                                    labelPlacement="end"
                                    sx={{ m: 0 }}
                                />
                            </Box>
                        </Box>

                        {/* Row 3: Reports + Add New Plant */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(is('Super Admin') || is('Admin') || is('Asset Management') || can('plant.create')) && (
                                <Button
                                    variant="contained"
                                    onClick={handlePlantreport}
                                    sx={{
                                        flex: 1,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        bgcolor: '#1976d2',
                                        '&:hover': { bgcolor: '#1565c0' },
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Reports
                                </Button>
                            )}
                            {(is('Super Admin') || is('Admin') || can('plant.create')) && (
                                <Button
                                    variant="contained"
                                    onClick={handleAddnewplant}
                                    sx={{
                                        flex: 1,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        bgcolor: '#1976d2',
                                        '&:hover': { bgcolor: '#1565c0' },
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    + Add Plant
                                </Button>
                            )}
                        </Box>

                        {/* Row 4: Plant Type Selector + Map Toggle */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <select
                                    className="selectbox"
                                    value={selectedValue}
                                    onChange={handleSelectChange}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '12px',
                                        border: '1px solid #eee',
                                        backgroundColor: '#fff',
                                        padding: '0 12px',
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}
                                >
                                    <option value="">Plant Type</option>
                                    <option value="Capex">Capex</option>
                                    <option value="Opex">Opex</option>
                                </select>
                            </Box>
                            <IconButton
                                onClick={handleSolarMapView}
                                sx={{
                                    bgcolor: '#1976d2',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    width: '40px',
                                    height: '40px',
                                    '&:hover': { bgcolor: '#1565c0' }
                                }}
                            >
                                <MapIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Collapse>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
                    <CircularProgress size={32} thickness={5} sx={{ color: '#1a1a1a' }} />
                </Box>
            ) : (
                <>
                    {currentRows.map((row) => (
                        <PlantCard
                            key={row.id}
                            row={row}
                            onClick={() => handleTechnicalView(row)}
                            issueCount={issueCounts[row.id]}
                            handleViewIssues={handleViewIssues}
                            isMonitoring={isMonitoring}
                            deviceStatusForRow={deviceStatus[row.id]}
                        />
                    ))}

                    {currentRows.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 10, bgcolor: '#fff', borderRadius: '20px' }}>
                            <Typography color="text.secondary" sx={{ fontWeight: 500 }}>No plants found matching your criteria</Typography>
                        </Box>
                    )}

                    {totalPlants > 0 && (
                        <TablePagination
                            component="div"
                            count={totalPlants}
                            page={currentPage - 1}
                            onPageChange={(e, newPage) => setCurrentPage(newPage + 1)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10))
                                setCurrentPage(1)
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            labelRowsPerPage="Rows:"
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                '& .MuiTablePagination-toolbar': {
                                    px: { xs: 0.5, sm: 2 },
                                    minHeight: 52,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                                    display: { xs: 'grid', sm: 'flex' },
                                    gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'unset' },
                                    alignItems: 'center',
                                },
                                '& .MuiTablePagination-spacer': {
                                    display: { xs: 'none', sm: 'block' },
                                },
                                '& .MuiTablePagination-selectLabel': {
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    mb: 0,
                                },
                                '& .MuiTablePagination-select': {
                                    fontSize: { xs: '0.72rem', sm: '0.875rem' },
                                    mr: { xs: 0.25, sm: 2 },
                                    lineHeight: 1,
                                },
                                '& .MuiTablePagination-selectRoot': {
                                    mr: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    gridColumn: { xs: '1', sm: 'unset' },
                                    '&::before': {
                                        content: '"Rows:"',
                                        display: { xs: 'inline-block', sm: 'none' },
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        mr: 0.5,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1,
                                    },
                                },
                                '& .MuiTablePagination-displayedRows': {
                                    fontSize: { xs: '0.72rem', sm: '0.875rem' },
                                    ml: { xs: 0, sm: 'auto' },
                                    mr: { xs: 0.25, sm: 1 },
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                    gridColumn: { xs: '2', sm: 'unset' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    lineHeight: 1,
                                },
                                '& .MuiTablePagination-actions': {
                                    ml: 0,
                                    flexShrink: 0,
                                    gridColumn: { xs: '3', sm: 'unset' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& .MuiIconButton-root': {
                                        padding: { xs: '1px', sm: '8px' },
                                    }
                                }
                            }}
                        />
                    )}
                </>
            )}
        </Box>
    )
}

export default SolarPlantListMobileView
